/**
 * Рубежи на карте СО СВОЕЙ СБОРКОЙ MapLibre: границы ИЭЗ, промысловые районы и
 * запретные районы одним вызовом.
 *
 * `MapCanvas` («Карты», «Аналитика») кладёт их сам; остальные разделы («Буи»,
 * «Скаут», «Спутники», карта молчания судов) строят карту своими руками, и до
 * 19.09.2026 каждый носил СВОИ переходники: у «Буёв» были одни границы ИЭЗ, у
 * «Спутников» — ничего (жалоба владельца: «промысловые зоны и границы РФ должны
 * быть доступны на всех картах, особенно с буями»). Три переходника на раздел,
 * переписанные трижды, — ровно та форма, в которой один раздел однажды
 * отстаёт молча. Отсюда один хук, а сторож (`boundaryOverlays.test.ts`) требует
 * его от КАЖДОГО файла, создающего карту.
 *
 * Логика «что просить и когда» остаётся в трёх общих хуках (`useEezLayer`,
 * `useDistrictsLayer`, `useBansLayer`); здесь только переходники к карте.
 */
import { useRef } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'

import type { BBox } from '../api'
import { applyBanLayers, removeBanLayers } from './bansLayer'
import { DISTRICT_LAYER_IDS, applyDistrictLayers, removeDistrictLayers } from './districtsLayer'
import { applyEezLayers, removeEezLayers } from './eezLayer'
import type { StyleGate } from './styleGate'
import { useBansLayer, type BanMapSink, type BanMeta } from './useBansLayer'
import { useDistrictsLayer, type DistrictMapSink } from './useDistrictsLayer'
import { useEezLayer, type EezMapSink } from './useEezLayer'

/** Кадр карты → рамка запроса. Долгота приводится к знаковой на сервере: за
 *  180-м меридианом MapLibre отдаёт непрерывную восточную. */
export function boundsToBox(map: maplibregl.Map): BBox {
  const b = map.getBounds()
  return { lat_min: b.getSouth(), lat_max: b.getNorth(), lon_min: b.getWest(), lon_max: b.getEast() }
}

/** Ключ кадра для перезапроса рубежей. */
export const boxKey = (box: BBox | null): string =>
  box ? `${box.lat_min},${box.lat_max},${box.lon_min},${box.lon_max}` : ''

/** Что включено. Промысловые районы и запреты — ОДИН тумблер (решение
 *  владельца 18.09.2026: «на том же слое»). */
export interface BoundaryToggles {
  eez: boolean
  districts: boolean
}

export interface BoundaryMeta {
  eezAttribution: string | null
  eezDisclaimerKey: string | null
  districtsDisclaimerKey: string | null
  bans: BanMeta
}

interface Options {
  mapRef: RefObject<maplibregl.Map | null>
  gateRef: RefObject<StyleGate>
  on: BoundaryToggles
  viewKey: string
  /** Слой страницы, ПОД который ложатся рубежи: отметки, платформы, спутники.
   *  Линия поверх них перечёркивала бы ровно то, ради чего раздел открыт. */
  beforeId?: string
  /** Дата и вид — только запретам: запрет либо в силе для этого кадра, либо
   *  нет. Без даты сервер берёт сегодня, без вида показывает все. */
  date?: string | null
  species?: number | null
}

export function useBoundaryOverlays(o: Options): BoundaryMeta {
  const opts = useRef(o)
  opts.current = o

  const sinks = useRef<{ eez: EezMapSink; fd: DistrictMapSink; fb: BanMapSink } | null>(null)
  if (!sinks.current) {
    const map = () => opts.current.mapRef.current
    const run = (fn: (m: maplibregl.Map) => void) => {
      const m = map()
      // Через шлюз стиля: ответ, пришедший до разбора стиля, иначе терялся бы
      // молча — та же ловушка, что у слоёв самой страницы.
      if (m) opts.current.gateRef.current?.run(() => fn(m))
    }
    const bbox = (): BBox => {
      const m = map()
      return m ? boundsToBox(m) : { lat_min: 0, lat_max: 0, lon_min: 0, lon_max: 0 }
    }
    const below = () => opts.current.beforeId
    sinks.current = {
      eez: {
        getMap: map, getBBox: bbox,
        setEez: data => run(m => applyEezLayers(m, data, below())),
        removeEez: () => { const m = map(); if (m) removeEezLayers(m) },
      },
      fd: {
        getMap: map, getBBox: bbox,
        setDistricts: data => run(m => applyDistrictLayers(m, data, below())),
        removeDistricts: () => { const m = map(); if (m) removeDistrictLayers(m) },
      },
      fb: {
        getMap: map, getBBox: bbox,
        // Под линии районов, если они уже лежат: номер района читается сквозь
        // бледную заливку запрета.
        setBans: data => run(m => applyBanLayers(
          m, data, m.getLayer(DISTRICT_LAYER_IDS[0]) ? DISTRICT_LAYER_IDS[0] : below())),
        removeBans: () => { const m = map(); if (m) removeBanLayers(m) },
      },
    }
  }
  const s = sinks.current
  const eezRef = useRef(s.eez)
  const fdRef = useRef(s.fd)
  const fbRef = useRef(s.fb)

  const eez = useEezLayer(eezRef, o.on.eez, o.viewKey)
  const fd = useDistrictsLayer(fdRef, o.on.districts, o.viewKey)
  const bans = useBansLayer(fbRef, o.on.districts, o.viewKey, o.date ?? null, o.species ?? null)

  return {
    eezAttribution: eez.attribution,
    eezDisclaimerKey: eez.disclaimerKey,
    districtsDisclaimerKey: fd.disclaimerKey,
    bans,
  }
}
